import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import { useForm } from 'react-hook-form';
import { formActions, httpRequestsOnSuccessActions } from 'store';
import { CreateChancel, Loader, MinLoader, NoItemsYet } from 'components';
import { FindLoad, FindSuccessItem, Images, noteStatusEnums } from 'utils';
import { ReportBuilder } from './progressReport/reportBuilder';
import { FormBuilderProvider, useFormBuilder } from './formDetail';
import { useAutoFillMentions } from './hooks/useAutoFillMentions';
import { formModelEnums } from './constants';

const FORM_ACTION_TYPE = 'GET_FORMS';
const DEFAULT_FORM_ACTION_TYPE = 'GET_FORM_BY_ID';
const DEFAULT_FORM_VALUES_ACTION_TYPE = 'GET_FORMS_DEFAULT';

const noTemplateYet = {
    title: 'No ABC Data template yet',
    subTitle: 'Publish an ABC Data template in Templates before collecting ABC data.',
};

/**
 * ABC Data entry form.
 *
 * Sibling of `sessionNoteFormBuilder` rather than a mode of it: ABC notes carry a Draft/Completed
 * label with NO review workflow (no IN_REVIEW, no supervisor approval, no lock-after-submit, no
 * update requests), and the org has exactly one ABC template so there is no template picker.
 * Everything the two genuinely share — `FormBuilderProvider`, `ReportBuilder`, the `statusRef`
 * status channel, autofill — is reused here.
 */
const AbcFormBuilderInner = forwardRef(({ info, resourceId, ACTION_TYPE, handleSaveNote, afterSuccess }, ref) => {
    const { setFormList, setImageUrls } = useFormBuilder();
    const { watch, handleSubmit, setValue, getValues, reset } = useForm({ mode: 'onChange', reValidateMode: 'onChange' });
    const dispatch = useDispatch();

    const forms = useSelector((state) => state.forms.forms)?.data;
    const formById = useSelector((state) => state.forms.form);

    const loader = FindLoad(ACTION_TYPE);
    const loadForms = FindLoad(FORM_ACTION_TYPE);
    const loadForm = FindLoad(DEFAULT_FORM_ACTION_TYPE);
    const loadDefaults = FindLoad(DEFAULT_FORM_VALUES_ACTION_TYPE);
    const success = FindSuccessItem(ACTION_TYPE);

    const reportBuilderRef = useRef(null);
    const statusRef = useRef(null);
    const [isDraftSaving, setIsDraftSaving] = useState(false);
    const [onLoadFile, setOnLoadFile] = useState(false);
    const [formsLoaded, setFormsLoaded] = useState(false);
    const applyAutoFill = useAutoFillMentions({ setValue, getValues });

    const isLoading = !!loadForm?.length || !!loadDefaults?.length;
    // Editing an already-COMPLETED entry keeps it COMPLETED — saving content edits must never
    // push a submitted entry back to Draft.
    const isCompleted = info?.id && info?.status === noteStatusEnums.COMPLETED;
    // Only after the template fetch has actually resolved — otherwise the empty state flashes
    // on the first render, before `getForms` has been dispatched.
    const noTemplate = formsLoaded && !loadForms?.length && !forms?.length && !info?.id;

    useEffect(
        () => () => {
            dispatch(formActions.resetFormById());
            dispatch(formActions.resetFormDefault());
        },
        [dispatch]
    );

    // Exactly one ABC template may exist per org (enforced in FormBuilderService.createForm), so
    // the first published one is auto-selected silently — no picker is rendered. The list itself
    // is fetched by the host (like every other note surface), not here: this builder remounts on
    // every entry selection, and re-fetching templates each time would be pure waste.
    useEffect(() => {
        if (forms) setFormsLoaded(true);
        if (!forms?.length || info?.id || watch('customForm')) return;
        const abcForm = forms.find((item) => item?.default) || forms[0];
        if (!abcForm?.id) return;
        setValue('customForm', abcForm.id);
        const params = { model: formModelEnums.ABC_DATA, resourceId, formId: abcForm.id };
        dispatch(formActions.getFormDefault(abcForm.id, params));
        dispatch(formActions.getFormById(abcForm.id, params));
    }, [forms]);

    useEffect(() => {
        if (!formById?.id) return;
        // Signed image URLs belong in context, not in RHF values — they change on every read.
        const { imageUrls: formImageUrls, ...cloned } = cloneDeep(formById);
        setImageUrls?.(formImageUrls || {});
        setFormList(cloned);
        reset({ customForm: watch('customForm'), formId: formById.id, ...cloned });
        applyAutoFill();
    }, [formById]);

    useEffect(() => {
        if (!info?.template) return;
        const { imageUrls: infoImageUrls, ...tpl } = info.template || {};
        setImageUrls?.(infoImageUrls || {});
        setFormList(cloneDeep(tpl));
        reset({ ...tpl, customForm: info?.id });
        applyAutoFill();
    }, [info]);

    useEffect(() => {
        if (!success) return;
        dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
        afterSuccess?.();
    }, [success]);

    const handleSubmitEntry = () => {
        setIsDraftSaving(false);
        statusRef.current = noteStatusEnums.COMPLETED;
        reportBuilderRef.current?.submit();
    };

    const handleSaveDraft = () => {
        setIsDraftSaving(true);
        // An entry that is already Completed stays Completed when saved from the secondary action.
        statusRef.current = isCompleted ? noteStatusEnums.COMPLETED : noteStatusEnums.DRAFT;
        reportBuilderRef.current?.submitDraft();
    };

    useImperativeHandle(ref, () => ({
        submit: handleSubmitEntry,
        submitDraft: handleSaveDraft,
        isDirty: () => !!reportBuilderRef.current?.isDirty?.(),
        canAutoSave: () => !!reportBuilderRef.current?.isDirty?.(),
    }));

    if (noTemplate) {
        return <NoItemsYet height={'240px'} text={noTemplateYet.title} subTitle={noTemplateYet.subTitle} image={Images.clientAbcData} />;
    }

    return (
        <div className="form-builder">
            <form onSubmit={handleSubmit(handleSubmitEntry)}>
                {isLoading || !!loadForms?.length ? (
                    <Loader height={'240px'} />
                ) : (
                    watch('customForm') && (
                        <ReportBuilder
                            resourceId={resourceId}
                            ref={reportBuilderRef}
                            handleSaveNote={handleSaveNote}
                            statusRef={statusRef}
                            onSigUploadingChange={(e) => setOnLoadFile(e)}
                        />
                    )
                )}

                <div className="submit-form-actions">
                    <CreateChancel
                        noChancel
                        disabled={isLoading || !!loader.length || onLoadFile}
                        type="button"
                        loader={(!!loader.length || onLoadFile) && !isDraftSaving}
                        create={isCompleted ? 'Save Changes' : 'Submit'}
                        onCreate={handleSubmitEntry}
                        buttonWidth="100%"
                        renderSecondAction={() =>
                            !isCompleted && (
                                <button
                                    type="button"
                                    onClick={handleSaveDraft}
                                    className="save-as-draft-btn"
                                    disabled={isLoading || !!loader.length || onLoadFile}
                                >
                                    {(!!loader.length || onLoadFile) && isDraftSaving ? (
                                        <MinLoader margin={'0'} color={'#0B5DCF'} position={'relative'} />
                                    ) : info?.id ? (
                                        'Save Changes'
                                    ) : (
                                        'Save as Draft'
                                    )}
                                </button>
                            )
                        }
                    />
                </div>
            </form>
        </div>
    );
});

export const AbcFormBuilder = forwardRef((props, ref) => (
    <FormBuilderProvider>
        <AbcFormBuilderInner {...props} ref={ref} />
    </FormBuilderProvider>
));
